import { Instagram, Youtube, Linkedin, type LucideIcon } from 'lucide-react';
import { TikTokIcon } from '@/components/icons/TikTokIcon';
import type { ComponentType, SVGProps } from 'react';

export type SocialIcon = LucideIcon | ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

export interface SocialFieldDef {
  /** Column name in `public.profiles`. */
  key: 'tiktok_url' | 'instagram_url' | 'youtube_url' | 'linkedin_url';
  /** Form-state key (camelCase) used by ProfileHero. */
  formKey: 'tiktokUrl' | 'instagramUrl' | 'youtubeUrl' | 'linkedinUrl';
  label: string;
  icon: SocialIcon;
  placeholder: string;
  /** Hostnames that, if present, mark the URL as recognised. */
  hosts: string[];
}

/**
 * Canonical order for social network fields across the entire app
 * (profile editor, MemberProfileCard, future widgets). Update here only.
 */
export const SOCIAL_FIELDS: readonly SocialFieldDef[] = [
  {
    key: 'tiktok_url',
    formKey: 'tiktokUrl',
    label: 'TikTok',
    icon: TikTokIcon,
    placeholder: 'https://tiktok.com/@seuusuario',
    hosts: ['tiktok.com'],
  },
  {
    key: 'instagram_url',
    formKey: 'instagramUrl',
    label: 'Instagram',
    icon: Instagram,
    placeholder: 'https://instagram.com/seuusuario',
    hosts: ['instagram.com'],
  },
  {
    key: 'youtube_url',
    formKey: 'youtubeUrl',
    label: 'YouTube',
    icon: Youtube,
    placeholder: 'https://youtube.com/@seucanal',
    hosts: ['youtube.com', 'youtu.be'],
  },
  {
    key: 'linkedin_url',
    formKey: 'linkedinUrl',
    label: 'LinkedIn',
    icon: Linkedin,
    placeholder: 'https://linkedin.com/in/seuperfil',
    hosts: ['linkedin.com'],
  },
] as const;

export type SocialUrls = {
  tiktok_url: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  linkedin_url: string | null;
};

/** Tracking query params we strip from social URLs before persisting. */
const TRACKING_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  '_t', '_r', 'igshid', 'si', 'feature', 'fbclid', 'gclid',
];

/**
 * Normalise a user-typed social URL: trim, prepend https://, strip tracking
 * params, and validate against the platform's known hosts. Returns null
 * (not throws) if the value is empty or irrecoverable — callers should
 * persist null in that case.
 */
export function normalizeSocialUrl(value: string | null | undefined, field: SocialFieldDef): string | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  // Reject obviously dangerous schemes outright.
  if (/^(javascript|data|vbscript):/i.test(raw)) return null;

  // Prepend https:// if no scheme is provided.
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }

  // Validate hostname matches the platform.
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const matches = field.hosts.some(h => host === h || host.endsWith(`.${h}`));
  if (!matches) return null;

  // Strip tracking params.
  TRACKING_PARAMS.forEach(p => url.searchParams.delete(p));

  // Drop trailing "?" if no params remain.
  let result = url.toString();
  if (url.search === '') result = result.replace(/\?$/, '');
  // Drop trailing slash from bare host (keeps /path/ alone).
  if (url.pathname === '/' && !url.search && !url.hash) {
    result = result.replace(/\/$/, '');
  }
  return result;
}